import type { PermissionCode } from '@dob/domain';
import {
  Activity,
  BookOpen,
  Boxes,
  Building2,
  ClipboardCheck,
  FileText,
  FileSearch,
  House,
  Inbox,
  KeyRound,
  MessageSquareText,
  MonitorSmartphone,
  Package,
  PackageCheck,
  PackagePlus,
  PackageSearch,
  Scale,
  RefreshCwOff,
  Settings,
  Siren,
  Tags,
  Trash2,
  Truck,
  Users,
  Vault,
  type LucideIcon,
} from 'lucide-react';

import type { ar } from '@dob/i18n';

export type NavPath =
  | '/'
  | '/inbox'
  | '/cash'
  | '/users'
  | '/roles'
  | '/locations'
  | '/devices'
  | '/settings'
  | '/audit'
  | '/emergency'
  | '/approvals'
  | '/sync-issues'
  | '/messages'
  | '/health'
  | '/ledger'
  | '/inventory/items'
  | '/inventory/categories'
  | '/inventory/stock'
  | '/inventory/waste'
  | '/inventory/openings'
  | '/inventory/match'
  | '/purchases/suppliers'
  | '/purchases/invoices'
  | '/purchases/receive'
  | '/purchases/excess';

export interface NavItem {
  readonly to: NavPath;
  readonly label: keyof (typeof ar)['nav'];
  readonly icon: LucideIcon;
  /** Shown when the person holds any of these (at the selected location or company-wide). */
  readonly anyOf?: readonly PermissionCode[];
  /** Built in this version; the others show "Soon" and a placeholder page. */
  readonly ready: boolean;
}

export interface NavGroup {
  readonly label: keyof (typeof ar)['nav'];
  readonly items: readonly NavItem[];
}

export const NAV: readonly NavGroup[] = [
  {
    label: 'groupOverview',
    items: [
      { to: '/', label: 'dashboard', icon: House, ready: true },
      { to: '/inbox', label: 'inbox', icon: Inbox, ready: true },
    ],
  },
  {
    label: 'groupPeople',
    items: [
      { to: '/users', label: 'users', icon: Users, anyOf: ['user.view'], ready: true },
      { to: '/roles', label: 'roles', icon: KeyRound, anyOf: ['role.view'], ready: true },
    ],
  },
  {
    label: 'groupOrganization',
    items: [
      {
        to: '/locations',
        label: 'locations',
        icon: Building2,
        anyOf: ['location.view'],
        ready: true,
      },
      {
        to: '/devices',
        label: 'devices',
        icon: MonitorSmartphone,
        anyOf: ['device.view'],
        ready: true,
      },
      {
        to: '/settings',
        label: 'settings',
        icon: Settings,
        anyOf: ['settings.view'],
        ready: true,
      },
    ],
  },
  {
    label: 'groupControl',
    items: [
      {
        to: '/approvals',
        label: 'approvals',
        icon: ClipboardCheck,
        anyOf: ['approval.view'],
        ready: true,
      },
      {
        to: '/sync-issues',
        label: 'syncIssues',
        icon: RefreshCwOff,
        anyOf: ['sync.issue.view'],
        ready: true,
      },
      { to: '/audit', label: 'audit', icon: FileSearch, anyOf: ['audit.view'], ready: true },
      {
        to: '/emergency',
        label: 'emergency',
        icon: Siren,
        anyOf: ['emergency.activate', 'emergency.end'],
        ready: true,
      },
      {
        to: '/messages',
        label: 'messages',
        icon: MessageSquareText,
        anyOf: ['notification.view_log'],
        ready: true,
      },
      { to: '/health', label: 'health', icon: Activity, anyOf: ['backup.view'], ready: true },
    ],
  },
  {
    label: 'groupWarehouse',
    items: [
      {
        to: '/inventory/items',
        label: 'items',
        icon: Package,
        anyOf: ['inventory.view'],
        ready: true,
      },
      {
        to: '/inventory/stock',
        label: 'stockHere',
        icon: Boxes,
        anyOf: ['inventory.view'],
        ready: true,
      },
      {
        to: '/inventory/categories',
        label: 'itemCategories',
        icon: Tags,
        anyOf: ['inventory.view'],
        ready: true,
      },
      {
        to: '/inventory/waste',
        label: 'waste',
        icon: Trash2,
        anyOf: ['inventory.view'],
        ready: true,
      },
      {
        to: '/inventory/openings',
        label: 'openings',
        icon: PackagePlus,
        anyOf: ['inventory.opening.prepare', 'inventory.cost.view'],
        ready: true,
      },
      {
        to: '/inventory/match',
        label: 'stockMatch',
        icon: Scale,
        anyOf: ['inventory.cost.view'],
        ready: true,
      },
    ],
  },
  {
    label: 'groupPurchases',
    items: [
      {
        to: '/purchases/invoices',
        label: 'purchaseInvoices',
        icon: FileText,
        anyOf: ['purchase.view', 'purchase.record'],
        ready: true,
      },
      {
        to: '/purchases/receive',
        label: 'receiveGoods',
        icon: PackageCheck,
        anyOf: ['purchase.receive'],
        ready: true,
      },
      {
        to: '/purchases/excess',
        label: 'receiptExcess',
        icon: PackageSearch,
        anyOf: ['purchase.discrepancy.resolve'],
        ready: true,
      },
      {
        to: '/purchases/suppliers',
        label: 'suppliers',
        icon: Truck,
        anyOf: ['supplier.view'],
        ready: true,
      },
    ],
  },
  {
    label: 'groupFinance',
    items: [
      { to: '/ledger', label: 'ledger', icon: BookOpen, anyOf: ['ledger.view'], ready: true },
      { to: '/cash', label: 'cash', icon: Vault, anyOf: ['cashbox.view'], ready: true },
    ],
  },
];

/** The navigation a person sees: only what they may open; empty groups disappear. */
export function visibleNav(can: (code: PermissionCode) => boolean): NavGroup[] {
  return NAV.map((group) => ({
    ...group,
    items: group.items.filter((item) => !item.anyOf || item.anyOf.some((code) => can(code))),
  })).filter((group) => group.items.length > 0);
}
